import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Select from "react-select";
import { Icon } from "@iconify/react";
import DateTimePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import { enGB } from "date-fns/locale";

import NavHeader from "./NavHeader";
import LoadingSpinner from "./LoadingSpinner";
import CustomAlert from "./CustomAlert";
import CustomAlertWithButtons from "./CustomAlertWithButtons";
import Avatar from "./Avatar";
import useUsers from "./hooks/useUsers";

import { useAuth } from "../core/AuthContext";
import { useSemester } from "../core/SemesterContext";
import { useDepartments } from "../core/DepartmentsContext";
import { URL } from "../core/config";
import {
  canEditEvent,
  hasPermission,
  isEventOrganizer,
  isWithinOrganizerEditWindow,
} from "../core/utils/permissionUtils";
import { parseJustifiedAbsencesFromEvent } from "../core/utils/generalMeetingUtils";
import {
  getDepartmentsForContext,
  extractDepartmentIds,
  getDepartmentName,
  resolveDepartmentRef,
} from "../core/constants/departments";

import "../styles/CreateEventWizard.css";
import "../styles/userlist.css";
import "../styles/general.css";

const eventsURL = `${URL}/events`;
const HR_DEPARTMENT_NAME = "Human Resources";

registerLocale("en-GB", enGB);

const extractId = (item) => {
  if (!item) return null;
  return typeof item === "object" ? item._id : item;
};

const extractIds = (items) => {
  if (!items || !Array.isArray(items)) return [];
  return items.map((item) => extractId(item)).filter(Boolean);
};

const excludeOrganizersFromParticipants = (participants, mos, vmos) => {
  const organizerIds = new Set([...mos, ...vmos]);
  return participants.filter((id) => !organizerIds.has(id));
};

function EditEvent() {
  const history = useHistory();
  const { eventId } = useParams();
  const { loading: authLoading, user: contextUser } = useAuth();
  const { departments: catalog } = useDepartments();
  useSemester();

  const [eventData, setEventData] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [staffSearchQuery, setStaffSearchQuery] = useState("");
  const [alumniSearchQuery, setAlumniSearchQuery] = useState("");
  const [alumniRoster, setAlumniRoster] = useState([]);
  const [alumniRosterLoading, setAlumniRosterLoading] = useState(false);
  const [gmJustificationMode, setGmJustificationMode] = useState(false);
  const [selectedJustificationUserId, setSelectedJustificationUserId] = useState(null);
  const [justificationDraft, setJustificationDraft] = useState("");
  const [editingWasJustified, setEditingWasJustified] = useState(false);

  const referencedUserIds = useMemo(() => {
    if (!eventData) return [];
    return [
      eventData.mo,
      eventData.vmo,
      ...(eventData.mos || []),
      ...(eventData.vmos || []),
      ...(eventData.participants || []),
      ...(eventData.alumni || []),
      ...((eventData.justifiedAbsences || []).map((entry) => entry?.user || entry)),
    ];
  }, [eventData]);

  const {
    users,
    findUser,
    optionsForSelection,
    loading: usersLoading,
    formatUserDisplayName,
    loadAlumniUsers,
  } = useUsers({ resolveIds: referencedUserIds });

  useEffect(() => {
    if (!eventData || eventData.isGeneralMeeting) {
      setAlumniRoster([]);
      setAlumniRosterLoading(false);
      return undefined;
    }

    let cancelled = false;
    setAlumniRosterLoading(true);
    loadAlumniUsers()
      .then((rows) => {
        if (!cancelled) setAlumniRoster(rows);
      })
      .catch(() => {
        if (!cancelled) setAlumniRoster([]);
      })
      .finally(() => {
        if (!cancelled) setAlumniRosterLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [eventData, loadAlumniUsers]);

  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: "",
    message: "",
  });

  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: "",
    type: "",
    message: "",
    btn1Action: () => {},
    btn2Action: () => {},
    btn1Text: "",
    btn2Text: "",
  });

  const departmentOptions = useMemo(
    () =>
      getDepartmentsForContext("eventForm", catalog).map((dept) => ({
        value: dept._id ? String(dept._id) : dept.name,
        label: dept.name,
      })),
    [catalog],
  );

  const hrDepartment = useMemo(
    () => resolveDepartmentRef(HR_DEPARTMENT_NAME, catalog),
    [catalog],
  );
  const hrDepartmentId = hrDepartment?._id ? String(hrDepartment._id) : undefined;
  const hrDepartmentName = hrDepartment?.name ?? HR_DEPARTMENT_NAME;

  const isGeneralMeeting = Boolean(eventData?.isGeneralMeeting);

  useEffect(() => {
    let cancelled = false;

    setEventData(null);
    setFetchError(null);
    setEventLoading(true);
    setGmJustificationMode(false);
    setSelectedJustificationUserId(null);
    setJustificationDraft("");
    setEditingWasJustified(false);

    const fetchEventById = async (id, attempt = 0) => {
      try {
        const response = await axios.get(`${eventsURL}/${id}`);
        return response.data;
      } catch (err) {
        const is404 = err.response?.status === 404;
        if (is404 && attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
          return fetchEventById(id, attempt + 1);
        }
        throw err;
      }
    };

    const loadEvent = async () => {
      try {
        const data = await fetchEventById(eventId);
        if (!cancelled) setEventData(data);
      } catch (err) {
        if (!cancelled) {
          setEventData(null);
          setFetchError(
            err.response?.data?.error ||
              "Failed to load this event. It may have been deleted.",
          );
        }
      } finally {
        if (!cancelled) setEventLoading(false);
      }
    };

    loadEvent();

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const handleDiscardChanges = () => {
    setAlertActionInfo({
      show: true,
      title: "Discard changes?",
      type: "error",
      message:
        "Are you sure you want to discard all changes? Any unsaved edits will be lost.",
      btn1Action: () =>
        setAlertActionInfo((prev) => ({ ...prev, show: false })),
      btn2Action: () => {
        history.push("/events-list");
        setAlertActionInfo((prev) => ({ ...prev, show: false }));
      },
      btn1Text: "Keep editing",
      btn2Text: "Discard",
    });
  };

  if (eventLoading || authLoading) {
    return (
      <div className="page-container">
        <NavHeader />
        <LoadingSpinner message="Loading event" size="sm" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="page-container">
        <NavHeader />
        <div className="page-title-row">
          <h1 className="page-title page-title-icon">
            <span className="main-section-icon">
              <span
                className="main-section-icon-fill"
                style={{ background: "rgba(0, 174, 239, 0.15)", color: "#00aeef" }}
              >
                <Icon icon="mdi:pencil" className="icon" />
              </span>
            </span>
            Edit Event
          </h1>
        </div>
        <div className="edit-event-permission-denied">
          <p>{fetchError}</p>
          <Link to="/events-list" className="button">
            Back to Events List
          </Link>
        </div>
      </div>
    );
  }

  if (usersLoading) {
    return (
      <div className="page-container">
        <NavHeader />
        <LoadingSpinner message="Loading members" size="sm" />
      </div>
    );
  }

  const canEdit = canEditEvent(contextUser, eventData);

  if (!canEdit) {
    const organizerGraceExpired =
      isEventOrganizer(contextUser, eventData) &&
      !hasPermission(contextUser, "EDIT_EVENT") &&
      eventData?.date &&
      !isWithinOrganizerEditWindow(eventData.date);

    return (
      <div className="page-container">
        <NavHeader />
        <div className="edit-event-permission-denied">
          <p>
            {organizerGraceExpired
              ? "Organizers can only edit this event for one week after it takes place. Contact someone with event edit permissions if changes are still needed."
              : "You do not have permission to edit this event."}
          </p>
          <Link to="/events-list" className="button">
            Back to Events List
          </Link>
        </div>
      </div>
    );
  }

  if (!eventData?.name) {
    return (
      <div className="page-container">
        <NavHeader />
        <div className="edit-event-permission-denied">
          <p>Event not found.</p>
          <Link to="/events-list" className="button">
            Back to Events List
          </Link>
        </div>
      </div>
    );
  }

  const pageTitle = isGeneralMeeting
    ? `Edit General Meeting`
    : `Edit Event`;

  return (
    <div className="page-container">
      <NavHeader />

      <div className="page-title-row">
        <h1 className="page-title page-title-icon">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: "rgba(0, 174, 239, 0.15)", color: "#00aeef" }}
            >
              <Icon icon="mdi:pencil" className="icon" />
            </span>
          </span>
          {pageTitle}
        </h1>

        <div className="title-action-buttons">
          <Link
            to="/events-list"
            className="title-icon-button"
            aria-label="Back to Events List"
          >
            <Icon icon="mdi:arrow-left" />
          </Link>
        </div>
      </div>

      <div className="edit-event-card event-wizard-modal">
        <div className="event-wizard-header">
          <span className="event-wizard-header-title">
            <Icon icon="mdi:calendar-edit" />
            {eventData.name}
          </span>
        </div>

        <Formik
          initialValues={{
            name: eventData.name || "",
            date: eventData.date ? new Date(eventData.date) : null,
            time: eventData.date ? new Date(eventData.date) : null,
            meetingPoint: eventData.meetingPoint || "",
            departments: extractDepartmentIds(eventData.departments, catalog),
            mos: extractIds(eventData.mos),
            vmos: extractIds(eventData.vmos),
            participants: excludeOrganizersFromParticipants(
              extractIds(eventData.participants),
              extractIds(eventData.mos),
              extractIds(eventData.vmos),
            ),
            alumni: extractIds(eventData.alumni),
            justifiedAbsences: parseJustifiedAbsencesFromEvent(eventData),
            participantsCount: eventData.participantsCount ?? "",
            taiga: eventData.taiga || "",
            budget: eventData.budget || "",
            eventupp: eventData.eventupp || "",
            esnActivities: eventData.esnActivities || "",
            openCallSpread: eventData.openCallSpread || "",
          }}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur
          validationSchema={Yup.object().shape({
            name: Yup.string().required("Event name is required."),
            date: Yup.date().nullable().required("Event date is required."),
            time: Yup.date().nullable().required("Event time is required."),
            meetingPoint: Yup.string(),
            departments: Yup.array().of(Yup.string()),
            mos: Yup.array().of(Yup.string()),
            vmos: Yup.array().of(Yup.string()),
            participants: Yup.array().of(Yup.string()).nullable(),
            alumni: Yup.array().of(Yup.string()).nullable(),
            justifiedAbsences: Yup.array().of(
              Yup.object({
                userId: Yup.string().required(),
                justification: Yup.string(),
              }),
            ),
            participantsCount: Yup.number()
              .transform((value, originalValue) =>
                originalValue === "" ? undefined : value,
              )
              .integer("Must be a whole number")
              .min(0, "Must be 0 or greater")
              .nullable(),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              let finalDate = values.date;
              if (values.date && values.time) {
                finalDate = new Date(values.date);
                finalDate.setHours(
                  values.time.getHours(),
                  values.time.getMinutes(),
                );
              }

              const baseValues = {
                name: values.name,
                date: finalDate,
                meetingPoint: values.meetingPoint,
                participants: excludeOrganizersFromParticipants(
                  values.participants,
                  values.mos,
                  values.vmos,
                ),
              };

              const submitValues = isGeneralMeeting
                ? {
                    ...baseValues,
                    mos: [],
                    vmos: [],
                    justifiedAbsences: values.justifiedAbsences
                      .filter((entry) => entry.justification?.trim())
                      .map((entry) => ({
                        user: entry.userId,
                        justification: entry.justification.trim(),
                      })),
                    ...(hrDepartmentId && { departments: [hrDepartmentId] }),
                  }
                : {
                    ...baseValues,
                    departments: values.departments,
                    mos: values.mos,
                    vmos: values.vmos,
                    participantsCount:
                      values.participantsCount === ""
                        ? 0
                        : values.participantsCount,
                    alumni: values.alumni,
                    taiga: values.taiga,
                    budget: values.budget,
                    eventupp: values.eventupp,
                    esnActivities: values.esnActivities,
                    openCallSpread: values.openCallSpread,
                  };

              await axios.put(`${eventsURL}/${eventId}`, submitValues);
              setSubmitting(false);
              setAlertInfo({
                show: true,
                type: "success",
                message: isGeneralMeeting
                  ? "General Meeting was successfully updated!"
                  : "The event was successfully updated!",
              });
              setTimeout(() => history.push("/events-list"), 2000);
            } catch (error) {
              const serverMessage = error.response?.data?.error;
              setAlertInfo({
                show: true,
                type: "error",
                message: serverMessage
                  ? `Error updating the event: ${serverMessage}`
                  : `Error updating the event: ${error.message}. Please try again.`,
              });
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => {
            const toggleParticipant = (userId) => {
              const isSelected = values.participants.includes(userId);
              setFieldValue(
                "participants",
                isSelected
                  ? values.participants.filter((id) => id !== userId)
                  : [...values.participants, userId],
              );
            };

            const toggleAlumni = (userId) => {
              const isSelected = values.alumni.includes(userId);
              setFieldValue(
                "alumni",
                isSelected
                  ? values.alumni.filter((id) => id !== userId)
                  : [...values.alumni, userId],
              );
            };

            const hasSavedGmJustification = (userId) =>
              values.justifiedAbsences.some(
                (entry) =>
                  entry.userId === userId && entry.justification.trim().length > 0,
              );

            const isGmUserPresent = (userId) =>
              values.participants.includes(userId) ||
              hasSavedGmJustification(userId);

            const isGmUserJustified = (userId) => hasSavedGmJustification(userId);

            const clearJustificationEditor = () => {
              setSelectedJustificationUserId(null);
              setJustificationDraft("");
              setEditingWasJustified(false);
            };

            const closeJustificationEditor = () => {
              const userId = selectedJustificationUserId;
              if (!userId) return;

              if (!justificationDraft.trim() && editingWasJustified) {
                setFieldValue(
                  "justifiedAbsences",
                  values.justifiedAbsences.filter((entry) => entry.userId !== userId),
                );
              }

              clearJustificationEditor();
            };

            const removeGmPresent = (userId) => {
              setFieldValue(
                "participants",
                values.participants.filter((id) => id !== userId),
              );
              setFieldValue(
                "justifiedAbsences",
                values.justifiedAbsences.filter((entry) => entry.userId !== userId),
              );
              if (selectedJustificationUserId === userId) {
                clearJustificationEditor();
              }
            };

            const toggleGmListUser = (userId) => {
              if (isGmUserPresent(userId)) {
                removeGmPresent(userId);
                return;
              }

              setFieldValue("participants", [...values.participants, userId]);
            };

            const selectChipForJustification = (userId) => {
              if (!isGmUserPresent(userId)) return;

              if (selectedJustificationUserId === userId) {
                closeJustificationEditor();
                return;
              }

              if (selectedJustificationUserId) {
                closeJustificationEditor();
              }

              const savedEntry = values.justifiedAbsences.find(
                (entry) => entry.userId === userId,
              );
              const savedJustification = savedEntry?.justification?.trim() || "";

              setSelectedJustificationUserId(userId);
              setJustificationDraft(savedJustification);
              setEditingWasJustified(Boolean(savedJustification));
            };

            const saveGmJustification = (userId) => {
              const trimmed = justificationDraft.trim();

              if (!trimmed) {
                closeJustificationEditor();
                return;
              }

              setFieldValue(
                "participants",
                values.participants.filter((id) => id !== userId),
              );
              setFieldValue("justifiedAbsences", [
                ...values.justifiedAbsences.filter((entry) => entry.userId !== userId),
                { userId, justification: trimmed },
              ]);
              clearJustificationEditor();
            };

            const gmPresentCount =
              values.participants.length +
              values.justifiedAbsences.filter((entry) =>
                entry.justification.trim(),
              ).length;

            const gmPresentChips = [
              ...values.participants.map((userId) => ({
                userId,
                type: "attended",
                justification: "",
              })),
              ...values.justifiedAbsences
                .filter((entry) => entry.justification.trim())
                .map(({ userId, justification }) => ({
                  userId,
                  type: "justified",
                  justification,
                })),
            ].sort((a, b) => {
              const nameA = (
                users.find((user) => user._id === a.userId)?.fullName ||
                findUser(a.userId)?.fullName ||
                ""
              ).toLowerCase();
              const nameB = (
                users.find((user) => user._id === b.userId)?.fullName ||
                findUser(b.userId)?.fullName ||
                ""
              ).toLowerCase();
              return nameA.localeCompare(nameB);
            });

            const organizerIds = new Set([...values.mos, ...values.vmos]);

            const syncParticipantsWithoutOrganizers = (mos, vmos) => {
              setFieldValue(
                "participants",
                excludeOrganizersFromParticipants(values.participants, mos, vmos),
              );
            };

            const filteredStaff = users.filter((user) => {
              if (!isGeneralMeeting && organizerIds.has(user._id)) return false;
              const query = staffSearchQuery.trim().toLowerCase();
              if (!query) return true;
              return (
                (user.fullName || "").toLowerCase().includes(query) ||
                (user.nickname || "").toLowerCase().includes(query)
              );
            });

            const filteredAlumni = alumniRoster.filter((user) => {
              const query = alumniSearchQuery.trim().toLowerCase();
              if (!query) return true;
              return (
                (user.fullName || "").toLowerCase().includes(query) ||
                (user.nickname || "").toLowerCase().includes(query)
              );
            });

            return (
              <Form onSubmit={handleSubmit}>
                <div className="event-wizard-body">
                  {isGeneralMeeting && (
                    <div className="event-wizard-toggle-row edit-event-type-row">
                      <div className="event-wizard-toggle-text">
                        <span className="event-wizard-toggle-label">
                          General Meeting
                        </span>
                        <span className="event-wizard-toggle-helper">
                          HR department event
                        </span>
                      </div>
                      <span className="edit-event-type-chip">General Meeting</span>
                    </div>
                  )}

                  <h2 className="edit-event-section-title">Details</h2>

                  <div className="event-wizard-field">
                    <label className="event-wizard-label" htmlFor="name">
                      Event name<span className="event-wizard-required">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      className={`event-wizard-input ${touched.name && errors.name ? "is-invalid" : ""}`}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      placeholder="e.g. Hike to Arrábida"
                    />
                    <div className="event-wizard-field-error">
                      {touched.name && errors.name ? errors.name : ""}
                    </div>
                  </div>

                  <div className="event-wizard-field">
                    <label className="event-wizard-label">
                      Date &amp; time<span className="event-wizard-required">*</span>
                    </label>
                    <div className="event-wizard-date-time-grid">
                      <DateTimePicker
                        selected={values.date}
                        onChange={(date) => setFieldValue("date", date)}
                        showTimeSelect={false}
                        dateFormat="dd/MM/yyyy"
                        locale="en-GB"
                        placeholderText="dd/mm/yyyy"
                        className={`event-wizard-input ${touched.date && errors.date ? "is-invalid" : ""}`}
                        wrapperClassName="event-wizard-picker-wrapper"
                        name="date"
                        popperProps={{ strategy: "fixed" }}
                        portalId="root-portal"
                      />
                      <DateTimePicker
                        selected={values.time}
                        onChange={(time) => setFieldValue("time", time)}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={5}
                        timeCaption="Time"
                        dateFormat="HH:mm"
                        locale="en-GB"
                        placeholderText="00:00"
                        className={`event-wizard-input ${touched.time && errors.time ? "is-invalid" : ""}`}
                        wrapperClassName="event-wizard-picker-wrapper"
                      />
                    </div>
                    <div className="event-wizard-field-error">
                      {(touched.date && errors.date) ||
                        (touched.time && errors.time) ||
                        ""}
                    </div>
                  </div>

                  <div className="event-wizard-field">
                    <label className="event-wizard-label" htmlFor="meetingPoint">
                      Meeting point/location
                    </label>
                    <input
                      id="meetingPoint"
                      type="text"
                      name="meetingPoint"
                      className="event-wizard-input"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.meetingPoint}
                      placeholder="e.g. Rossio Square"
                    />
                    <div className="event-wizard-field-error" />
                  </div>

                  <div className="event-wizard-field">
                    <label className="event-wizard-label">Department</label>
                    {isGeneralMeeting ? (
                      <div className="event-wizard-readonly">
                        <span className="event-wizard-readonly-value">
                          {hrDepartmentName}
                        </span>
                        <span className="event-wizard-readonly-caption">
                          Set by event type
                        </span>
                      </div>
                    ) : (
                      <Select
                        isMulti
                        classNamePrefix="event-wizard-select"
                        name="departments"
                        options={departmentOptions}
                        value={departmentOptions.filter((option) =>
                          values.departments.includes(option.value),
                        )}
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : [];
                          setFieldValue("departments", selectedValues);
                        }}
                        onBlur={handleBlur}
                        placeholder="Select departments..."
                        isClearable
                        isSearchable
                      />
                    )}
                    <div className="event-wizard-field-error" />
                  </div>

                  {!isGeneralMeeting && (
                    <>
                      <h2 className="edit-event-section-title">Organizers</h2>

                      <div className="event-wizard-field">
                        <label className="event-wizard-label">
                          Main Organizers (MOs)
                        </label>
                        <Select
                          isMulti
                          classNamePrefix="event-wizard-select"
                          name="mos"
                          options={optionsForSelection(values.mos)}
                          value={optionsForSelection(values.mos).filter((option) =>
                            values.mos.includes(option.value),
                          )}
                          onChange={(selectedOptions) => {
                            const selectedValues = selectedOptions
                              ? selectedOptions.map((option) => option.value)
                              : [];
                            setFieldValue("mos", selectedValues);
                            syncParticipantsWithoutOrganizers(
                              selectedValues,
                              values.vmos,
                            );
                          }}
                          onBlur={handleBlur}
                          placeholder="Select MOs..."
                          isClearable
                          isSearchable
                          isLoading={usersLoading}
                        />
                        <div className="event-wizard-field-error" />
                      </div>

                      <div className="event-wizard-field">
                        <label className="event-wizard-label">
                          Vice Main Organizers (VMOs)
                        </label>
                        <Select
                          isMulti
                          classNamePrefix="event-wizard-select"
                          name="vmos"
                          options={optionsForSelection(values.vmos)}
                          value={optionsForSelection(values.vmos).filter((option) =>
                            values.vmos.includes(option.value),
                          )}
                          onChange={(selectedOptions) => {
                            const selectedValues = selectedOptions
                              ? selectedOptions.map((option) => option.value)
                              : [];
                            setFieldValue("vmos", selectedValues);
                            syncParticipantsWithoutOrganizers(
                              values.mos,
                              selectedValues,
                            );
                          }}
                          onBlur={handleBlur}
                          placeholder="Select VMOs..."
                          isClearable
                          isSearchable
                          isLoading={usersLoading}
                        />
                        <div className="event-wizard-field-error" />
                      </div>
                    </>
                  )}

                  <h2 className="edit-event-section-title">
                    {isGeneralMeeting ? "Attendance" : "Staff"}
                  </h2>
                  <p className="event-wizard-staff-intro">
                    {isGeneralMeeting
                      ? "Mark members who attended the general meeting."
                      : "Staff are optional — you can update them now or later."}
                  </p>

                  {isGeneralMeeting && (
                    <label className="event-wizard-gm-justification-toggle">
                      <span className="event-wizard-gm-justification-toggle-text">
                        <span className="event-wizard-gm-justification-toggle-label">
                          Justified absence
                        </span>
                        <span className="event-wizard-gm-justification-toggle-helper">
                          Select a present member to add a justification
                        </span>
                      </span>
                      <span className="events-general-meetings-switch">
                        <input
                          type="checkbox"
                          checked={gmJustificationMode}
                          onChange={(event) => {
                            if (gmJustificationMode && selectedJustificationUserId) {
                              closeJustificationEditor();
                            }
                            setGmJustificationMode(event.target.checked);
                            if (!event.target.checked) {
                              clearJustificationEditor();
                            }
                          }}
                          aria-label={
                            gmJustificationMode
                              ? "Disable justified absence mode"
                              : "Enable justified absence mode"
                          }
                        />
                        <span className="events-general-meetings-switch-track">
                          <span className="events-general-meetings-switch-thumb" />
                        </span>
                      </span>
                    </label>
                  )}

                  <div className="event-wizard-selected-row">
                    <span className="event-wizard-selected-count">
                      {isGeneralMeeting
                        ? `${gmPresentCount} marked present`
                        : `${values.participants.length} selected`}
                    </span>
                    {(isGeneralMeeting ? gmPresentCount : values.participants.length) > 0 && (
                      <div className="event-wizard-chip-row">
                        {isGeneralMeeting
                          ? gmPresentChips.map(({ userId, type, justification }) => {
                              const user = users.find((u) => u._id === userId) || findUser(userId);
                              if (!user) return null;
                              const firstName = (
                                user.fullName ||
                                user.nickname ||
                                "User"
                              ).split(" ")[0];
                              const chipClassName = `event-wizard-chip event-wizard-chip-${type}${
                                gmJustificationMode ? " event-wizard-chip-selectable" : ""
                              }${
                                gmJustificationMode &&
                                selectedJustificationUserId === userId
                                  ? " is-selected"
                                  : ""
                              }`;

                              if (gmJustificationMode) {
                                return (
                                  <button
                                    key={`present-${userId}`}
                                    type="button"
                                    className={chipClassName}
                                    title={
                                      type === "justified" && justification
                                        ? justification
                                        : undefined
                                    }
                                    onClick={() => selectChipForJustification(userId)}
                                  >
                                    <Avatar user={user} size={18} />
                                    {firstName}
                                  </button>
                                );
                              }

                              return (
                                <span key={`present-${userId}`} className={chipClassName}>
                                  <Avatar user={user} size={18} />
                                  {firstName}
                                  <button
                                    type="button"
                                    onClick={() => removeGmPresent(userId)}
                                    aria-label={`Remove ${firstName}`}
                                  >
                                    ✕
                                  </button>
                                </span>
                              );
                            })
                          : values.participants.map((id) => {
                              const user = users.find((u) => u._id === id) || findUser(id);
                              if (!user) return null;
                              const firstName = (
                                user.fullName ||
                                user.nickname ||
                                "User"
                              ).split(" ")[0];
                              return (
                                <span key={id} className="event-wizard-chip">
                                  <Avatar user={user} size={18} />
                                  {firstName}
                                  <button
                                    type="button"
                                    onClick={() => toggleParticipant(id)}
                                    aria-label={`Remove ${firstName}`}
                                  >
                                    ✕
                                  </button>
                                </span>
                              );
                            })}
                      </div>
                    )}
                  </div>

                  {isGeneralMeeting &&
                    gmJustificationMode &&
                    selectedJustificationUserId && (
                      <div className="event-wizard-gm-justification-editor">
                        {(() => {
                          const selectedUser =
                            users.find((user) => user._id === selectedJustificationUserId) ||
                            findUser(selectedJustificationUserId);

                          if (!selectedUser || !isGmUserPresent(selectedJustificationUserId)) {
                            return null;
                          }

                          return (
                            <>
                              <label
                                className="event-wizard-label"
                                htmlFor="gm-justification-input"
                              >
                                Justification for{" "}
                                {formatUserDisplayName(selectedUser)}
                              </label>
                              <div className="event-wizard-gm-justification-input-row">
                                <input
                                  id="gm-justification-input"
                                  type="text"
                                  className="event-wizard-input event-wizard-gm-justification-input"
                                  placeholder="Explain why this absence is justified..."
                                  value={justificationDraft}
                                  onChange={(event) =>
                                    setJustificationDraft(event.target.value)
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      event.preventDefault();
                                      saveGmJustification(selectedJustificationUserId);
                                    }
                                  }}
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  className="event-wizard-gm-justification-save"
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() =>
                                    saveGmJustification(selectedJustificationUserId)
                                  }
                                  aria-label="Save justification"
                                  title="Save justification"
                                >
                                  <Icon icon="mdi:content-save" />
                                </button>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                  <div className="event-wizard-search-row">
                    <input
                      type="text"
                      className="event-wizard-input"
                      placeholder="Search members..."
                      value={staffSearchQuery}
                      onChange={(e) => setStaffSearchQuery(e.target.value)}
                    />
                    <div className="event-wizard-inline-actions">
                      <button
                        type="button"
                        className="event-wizard-inline-action"
                        onClick={() => {
                          if (isGeneralMeeting) {
                            setFieldValue(
                              "participants",
                              users.map((u) => u._id),
                            );
                            setFieldValue("justifiedAbsences", []);
                          } else {
                            setFieldValue(
                              "participants",
                              users
                                .filter((user) => !organizerIds.has(user._id))
                                .map((user) => user._id),
                            );
                          }
                        }}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        className="event-wizard-inline-action"
                        onClick={() => {
                          setFieldValue("participants", []);
                          if (isGeneralMeeting) {
                            setFieldValue("justifiedAbsences", []);
                          }
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {usersLoading ? (
                    <p className="event-wizard-staff-empty">Loading members...</p>
                  ) : (
                    <ul className="event-wizard-staff-list">
                      {filteredStaff.map((user) => {
                        const isSelected = isGeneralMeeting
                          ? isGmUserPresent(user._id)
                          : values.participants.includes(user._id);

                        return (
                          <li key={user._id} className="event-wizard-staff-row">
                            <label>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() =>
                                  isGeneralMeeting
                                    ? toggleGmListUser(user._id)
                                    : toggleParticipant(user._id)
                                }
                              />
                              <Avatar user={user} size={26} />
                              <span className="event-wizard-staff-name-block">
                                <span className="event-wizard-staff-display-name">
                                  {formatUserDisplayName(user)}
                                </span>
                                <span className="event-wizard-staff-handle">
                                  {user.nickname ? `@${user.nickname}` : ""}
                                </span>
                              </span>
                              <span className="event-wizard-staff-team">
                                {getDepartmentName(user.departments?.[0], catalog) ||
                                  ""}
                              </span>
                              {isGeneralMeeting && isGmUserJustified(user._id) && (
                                <span className="event-wizard-gm-justified-badge">
                                  Justified
                                </span>
                              )}
                            </label>
                          </li>
                        );
                      })}
                      {filteredStaff.length === 0 && (
                        <li className="event-wizard-staff-empty">No members found.</li>
                      )}
                    </ul>
                  )}

                  {!isGeneralMeeting && (
                    <>
                      <h2 className="edit-event-section-title">Alumni</h2>
                      <p className="event-wizard-staff-intro">
                        Alumni who helped at this event — optional, same as staff.
                      </p>

                      <div className="event-wizard-selected-row">
                        <span className="event-wizard-selected-count">
                          {values.alumni.length} selected
                        </span>
                        {values.alumni.length > 0 && (
                          <div className="event-wizard-chip-row">
                            {values.alumni.map((id) => {
                              const user =
                                alumniRoster.find((u) => u._id === id) || findUser(id);
                              if (!user) return null;
                              const firstName = (
                                user.fullName ||
                                user.nickname ||
                                "User"
                              ).split(" ")[0];
                              return (
                                <span key={id} className="event-wizard-chip">
                                  <Avatar user={user} size={18} />
                                  {firstName}
                                  <button
                                    type="button"
                                    onClick={() => toggleAlumni(id)}
                                    aria-label={`Remove ${firstName}`}
                                  >
                                    ✕
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="event-wizard-search-row">
                        <input
                          type="text"
                          className="event-wizard-input"
                          placeholder="Search alumni..."
                          value={alumniSearchQuery}
                          onChange={(e) => setAlumniSearchQuery(e.target.value)}
                        />
                        <div className="event-wizard-inline-actions">
                          <button
                            type="button"
                            className="event-wizard-inline-action"
                            onClick={() =>
                              setFieldValue(
                                "alumni",
                                alumniRoster.map((user) => user._id),
                              )
                            }
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="event-wizard-inline-action"
                            onClick={() => setFieldValue("alumni", [])}
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      {alumniRosterLoading ? (
                        <p className="event-wizard-staff-empty">Loading alumni...</p>
                      ) : (
                        <ul className="event-wizard-staff-list">
                          {filteredAlumni.map((user) => {
                            const isSelected = values.alumni.includes(user._id);
                            return (
                              <li key={user._id} className="event-wizard-staff-row">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleAlumni(user._id)}
                                  />
                                  <Avatar user={user} size={26} />
                                  <span className="event-wizard-staff-name-block">
                                    <span className="event-wizard-staff-display-name">
                                      {formatUserDisplayName(user)}
                                    </span>
                                    <span className="event-wizard-staff-handle">
                                      {user.nickname ? `@${user.nickname}` : ""}
                                    </span>
                                  </span>
                                  <span className="event-wizard-staff-team">
                                    {getDepartmentName(user.departments?.[0], catalog) ||
                                      ""}
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                          {filteredAlumni.length === 0 && (
                            <li className="event-wizard-staff-empty">No alumni found.</li>
                          )}
                        </ul>
                      )}

                      <h2 className="edit-event-section-title">Attendance</h2>

                      <div className="event-wizard-field">
                        <label className="event-wizard-label">
                          Number of participants (tickets sold)
                        </label>
                        <input
                          type="number"
                          name="participantsCount"
                          className={`event-wizard-input ${touched.participantsCount && errors.participantsCount ? "is-invalid" : ""}`}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.participantsCount}
                          placeholder="0"
                          min="0"
                          step="1"
                        />
                        <div className="event-wizard-field-error">
                          {touched.participantsCount && errors.participantsCount
                            ? errors.participantsCount
                            : ""}
                        </div>
                      </div>

                      <h2 className="edit-event-section-title">Event links</h2>

                      <div className="event-wizard-field">
                        <label className="event-wizard-label">Taiga link</label>
                        <input
                          type="text"
                          name="taiga"
                          className="event-wizard-input"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.taiga}
                          placeholder="Paste link here"
                        />
                      </div>

                      <div className="event-wizard-field">
                        <label className="event-wizard-label">
                          Budget report link
                        </label>
                        <input
                          type="text"
                          name="budget"
                          className="event-wizard-input"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.budget}
                          placeholder="Paste link here"
                        />
                      </div>

                      <div className="event-wizard-field">
                        <label className="event-wizard-label">EventUpp link</label>
                        <input
                          type="text"
                          name="eventupp"
                          className="event-wizard-input"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.eventupp}
                          placeholder="Paste link here"
                        />
                      </div>

                      <div className="event-wizard-field">
                        <label className="event-wizard-label">
                          ESN Activities link
                        </label>
                        <input
                          type="text"
                          name="esnActivities"
                          className="event-wizard-input"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.esnActivities}
                          placeholder="Paste link here"
                        />
                      </div>

                      <div className="event-wizard-field">
                        <label className="event-wizard-label">
                          Open call spread
                        </label>
                        <input
                          type="text"
                          name="openCallSpread"
                          className="event-wizard-input"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.openCallSpread}
                          placeholder="Paste link here (only if applicable)"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="event-wizard-footer">
                  <button
                    type="button"
                    className="event-wizard-btn-secondary"
                    onClick={handleDiscardChanges}
                  >
                    Discard changes
                  </button>
                  <button
                    type="submit"
                    className="event-wizard-btn event-wizard-btn-success"
                    disabled={isSubmitting || usersLoading}
                  >
                    Save changes
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>

      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss
          timeout={4000}
          onClose={() => setAlertInfo({ show: false, type: "", message: "" })}
        />
      )}

      {alertActionInfo.show && (
        <CustomAlertWithButtons
          title={alertActionInfo.title}
          type={alertActionInfo.type}
          message={alertActionInfo.message}
          onButton1Click={alertActionInfo.btn1Action}
          onButton2Click={alertActionInfo.btn2Action}
          button1Text={alertActionInfo.btn1Text}
          button2Text={alertActionInfo.btn2Text}
        />
      )}
    </div>
  );
}

export default EditEvent;
